// src/components/CoordinatesGrids/RestitutionCoordinates.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, Nav, NavItem, NavLink, CardBody, Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, CardFooter, Modal, ModalHeader } from 'reactstrap';
import OSelect from '../../../Components/OElements/OSelect';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { mimeTypeToIconMapping } from '../../../helpers/mime-type_to_icon';
import RestitutionCoordinatesModal from '../Modals/RestitutionCoordinatesModal';
import DeleteGridItemModal from '../Modals/DeleteGridItemModal';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { asArray } from '../../../utils/asArray';
import { addNewRestitutionCoordinates, updateRestitutionCoordinates, deleteRestitutionCoordinates } from '../../../slices/thunks';
import { toast } from 'react-toastify';

const RestitutionCoordinates = ({ restitutionCoordinatesList, setRestitutionCoordinatesList, formIsEdit, waterRightId }) => {
	const dispatch = useDispatch();

	// Selectores para los datos maestros
	const selectorUtmZone = createSelector(
		state => state.UtmZone,
		data => asArray(data?.itemUtmZoneList)
	);
	const utmZoneList = useSelector(selectorUtmZone);

	const selectorGeodeticDatum = createSelector(
		state => state.GeodeticDatum,
		data => asArray(data?.itemGeodeticDatumList)
	);
	const geodeticDatumList = useSelector(selectorGeodeticDatum);

	// Helper functions to get names from IDs
	const getUtmZoneName = (id) => {
		const item = utmZoneList.find(item => item._id === id);
		return item?.name || id || '-';
	};

	const getGeodeticDatumName = (id) => {
		const item = geodeticDatumList.find(item => item._id === id);
		return item?.name || id || '-';
	};

    const [isEdit, setIsEdit] = useState(false);
    const [modal, setModal] = useState(false);
    const [dataCoordinates, setDataCoordinates] = useState(null)
    const [toggleTab, setToggleTab] = useState(null);
    const [modalDelete, setModalDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Get current user from localStorage
	const getCurrentUser = () => {
		try {
			const authUser = JSON.parse(localStorage.getItem('authUser'));
			return authUser?.user?.email || authUser?.email || 'system';
		} catch (error) {
			console.error('Error getting current user:', error);
			return 'system';
		}
	};
    
	// Handle Save
	const handleSave = async (formData) => {
		try {
			const currentUser = getCurrentUser();

			if (!formIsEdit) {
				// CREATE mode: no API calls, keep in local state until form is submitted
				if (isEdit && dataCoordinates) {
					// Update existing local item by reference
					setRestitutionCoordinatesList(prevList =>
						prevList.map(item => item === dataCoordinates ? { ...formData } : item)
					);
				} else {
					// Add new item to local state
					setRestitutionCoordinatesList(prevList => [...prevList, { ...formData }]);
				}
			} else if (isEdit && dataCoordinates?._id) {
				// EDIT mode: update existing item in API
				const updatePayload = {
					...formData,
					_id: dataCoordinates._id,
					modification_user: currentUser,
					modification_date: new Date().toISOString()
				};

				const response = await dispatch(updateRestitutionCoordinates(updatePayload)).unwrap();

				// Update local state with response from API
				setRestitutionCoordinatesList(prevList =>
					prevList.map(item =>
						item._id === dataCoordinates._id ? response : item
					)
				);
			} else {
				// EDIT mode: add new item to API immediately with water_right_id
				const createPayload = {
					...formData,
					water_right_id: waterRightId,
					creation_user: currentUser,
					creation_date: new Date().toISOString()
				};

				const response = await dispatch(addNewRestitutionCoordinates(createPayload)).unwrap();

				// Add to local state with response from API (includes _id)
				setRestitutionCoordinatesList(prevList => [...prevList, response]);
			}
			handleToggleModal();
		} catch (error) {
			console.error('Error saving restitution coordinates:', error);
			// Toast notification is already handled in the thunk
		}
	};

	// Delete
	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			if (dataCoordinates?._id) {
				// If it has _id, it's saved in the database, so delete from API
				await dispatch(deleteRestitutionCoordinates(dataCoordinates)).unwrap();
				
				setRestitutionCoordinatesList(prevList => 
					prevList.filter(item => item._id !== dataCoordinates._id)
				);
			} else {
				// If no _id, just remove from local state (not saved yet)
				setRestitutionCoordinatesList(prevList => 
					prevList.filter(item => item !== dataCoordinates)
				);
			}
			handleCloseDeleteModal();
		} catch (error) {
			console.error('Error deleting restitution coordinates:', error);
			// Toast notification is already handled in the thunk
		} finally {
			setIsDeleting(false);
		}
	};

    // Update Data
    const handleClickEdit = water_rights => {
		setIsEdit(true);
        setDataCoordinates(water_rights);
        setModal(true);
    };

    // Delete Data
    const handleClickDelete = water_rights => {
        setDataCoordinates(water_rights);
        setModalDelete(true);
    };

    function handleToggleModal() {
        setModal(prev => !prev);
        // toggleTab('1');
        // validation.resetForm();
    }

    function handleCloseDeleteModal() {
        setModalDelete(false);
    }
    
    const columns = useMemo(
        () => [
            {
                header: 'Acciones', enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false, enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false,
                accessorKey: 'residue',
                columnDefType: 'display',
                 size: 'auto',
                Cell: cellProps => {
                    return (
                        <React.Fragment>
                            <div className='d-flex justify-content-center'>
                                <div className='flex-shrink-0'>
                                    <ul className='list-inline mb-0'>
                                        <li className='list-inline-item'>
                                            <a
                                                title={'Editar'}
                                                href='#'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickEdit(cellProps.row.original);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
                                            </a>
                                        </li>
                                        <li className='list-inline-item'>
                                            <a
                                                title={'Eliminar'}
                                                href='#'
                                                className='remove-item-btn'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickDelete(cellProps.row.original);
                                                }}
                                            >
                                                <i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                },
            },
            { header: 'Nombre Coordenada', accessorKey: 'name',  size: 'auto' },
            { header: 'UTM Norte Restitución', accessorKey: 'utm_north_restitution',  size: 'auto' },
            { header: 'UTM Este Restitución', accessorKey: 'utm_east_restitution',  size: 'auto' },
            { 
				header: 'Huso UTM', 
				accessorKey: 'utm_zone_id',  
				size: 'auto',
				Cell: ({ row }) => row.original.utm_zone?.name || getUtmZoneName(row.original.utm_zone_id) || '-'
			},
            { 
				header: 'Datum Geodésico', 
				accessorKey: 'geodetic_datum_id',  
				size: 'auto',
				Cell: ({ row }) => row.original.geodetic_datum?.name || getGeodeticDatumName(row.original.geodetic_datum_id) || '-'
			},
            { header: 'Latitud Restitución', accessorKey: 'latitude_restitution',  size: 'auto' },
            { header: 'Longitud Restitución', accessorKey: 'longitude_restitution',  size: 'auto' },
        ],
        [utmZoneList, geodeticDatumList]
    );

  return (
     <React.Fragment>
        <div className='d-flex align-items-center'>
            <h5 className='card-title mb-0 flex-grow-1 '>Lista de Coordenadas de Restitución</h5>
                <div className='flex-shrink-0'>
                    <div className='d-flex flex-wrap gap-2'>
                        <button
                            className='btn btn-label btn-primary'
                            onClick={() => {
                                setIsEdit(false);
                                setDataCoordinates({});
                                handleToggleModal();
                            }}
                            type='button'
                        >
                            <i className='ri-add-line label-icon'></i> Añadir Coordenada
                        </button>
                    </div>
                </div>
        </div> 
        <div className='mt-3 my-4'>
            <ODataGrid
                columns={columns}
                data={restitutionCoordinatesList || []}
            />
        </div>
        <RestitutionCoordinatesModal
            dataCoordinates={dataCoordinates}
            handleToggleModal={handleToggleModal}
            isEdit={isEdit}
            isOpen={modal}
            handleSave={handleSave}
        />
		
		<DeleteGridItemModal
			isOpen={modalDelete}
			title='¡Estás a punto de eliminar una Coordenada de Restitución!'
			description='Si deseas realizar esta acción debes hacer click en confirmar.'
			details={dataCoordinates && (
				<div className='small text-muted'>
					<p><strong>Coordenada:</strong> {dataCoordinates.name}</p>
					<p><strong>UTM Norte:</strong> {dataCoordinates.utm_north_restitution}</p>
					<p><strong>UTM Este:</strong> {dataCoordinates.utm_east_restitution}</p>
				</div>
			)}
			onCancel={handleCloseDeleteModal}
			onConfirm={handleDelete}
			isDeleting={isDeleting}
		/>
    </React.Fragment>
  );
};


export default RestitutionCoordinates;